import type { SnowplowDataAdapters, SnowplowSdkConfig } from '@nextmar/snowplow-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';

/** Z 现有 Snowplow 环境名称；prod 只在 SDK environment 中规范为 production。 */
export type ZeeloolSnowplowEnvironment = 'local' | 'dev' | 'test' | 'uat' | 'prod';

/** Z 集成层已经从 env、远程配置、设备和 App 判定中解析出的明确输入。 */
export interface ZeeloolSnowplowSdkConfigInput {
  /** ReportingCenter 按既有优先级解析后的最终开关。 */
  enabled: boolean;
  /** Z 环境提供的 collector 地址；缺失时不补默认值。 */
  collectorUrl?: string;
  /** Z 环境提供的 appId 基础值；缺失时不补 z_us。 */
  appId?: string;
  /** Z 环境名称；缺失时保留无效空值交由 SDK init 拒绝。 */
  environment?: ZeeloolSnowplowEnvironment;
  /** Next.js 已有设备判定结果。 */
  deviceType: EPlatform;
  /** Z 已有 App 判定结果。 */
  isInApp: boolean;
  /** 环境配置解析后的明确 gzip 开关。 */
  gzipEnabled: boolean;
  /** 触发时读取 Z 业务快照的同步适配器。 */
  adapters: SnowplowDataAdapters;
}

/** 已存在的平台/环境后缀；重建 appId 前只从字符串尾部移除一次。 */
const SNOWPLOW_APP_ID_SUFFIX_PATTERN = /_(?:pc|m)_(?:local|dev|test|uat|prod)$/;

/** SDK collector 固定 postPath 的业务侧尾目录。 */
const SNOWPLOW_COLLECTOR_PATH_SUFFIX = '/zlmkt-metrics';

/** 保留含 query/hash 的无效输入，其余 collector 逐段移除重复固定尾路径。 */
function normalizeCollectorUrl(value: string | undefined): string {
  const trimmedUrl = value?.trim() ?? '';

  // Collector 不接受 query/hash；保留输入交由 SDK 拒绝，避免静默改写为另一地址。
  if (trimmedUrl.includes('?') || trimmedUrl.includes('#')) {
    return trimmedUrl;
  }

  let normalizedUrl = trimmedUrl.replace(/\/+$/, '');

  // 仅当完整固定路径位于末尾时逐段移除，避免 SDK 再次规范化后仍产生重复路径。
  while (normalizedUrl.endsWith(SNOWPLOW_COLLECTOR_PATH_SUFFIX)) {
    normalizedUrl = normalizedUrl
      .slice(0, -SNOWPLOW_COLLECTOR_PATH_SUFFIX.length)
      .replace(/\/+$/, '');
  }

  return normalizedUrl;
}

/** 把 Z prod 映射为 SDK production；缺失值不得回退到任何环境。 */
function normalizeEnvironment(
  value: ZeeloolSnowplowEnvironment | undefined,
): SnowplowSdkConfig['environment'] {
  if (value === 'prod') return 'production';
  if (value) return value;

  // SDK 类型描述成功配置；这里故意保留运行时空值，使 SDK init 按合同返回 invalid_config。
  return '' as SnowplowSdkConfig['environment'];
}

/** 使用明确基础值、设备段和 Z 环境后缀构造最终 appId。 */
function resolveAppId(
  appId: string | undefined,
  deviceType: EPlatform,
  environment: ZeeloolSnowplowEnvironment | undefined,
): string {
  const appIdBase = (appId?.trim() ?? '').replace(SNOWPLOW_APP_ID_SUFFIX_PATTERN, '');
  if (!appIdBase || !environment) return '';

  const deviceSegment = deviceType === EPlatform.WEB ? 'pc' : 'm';
  return `${appIdBase}_${deviceSegment}_${environment}`;
}

/**
 * 将 Z 已解析业务输入显式映射为 SDK 配置，不读取 env、global、storage 或 Store。
 */
export function resolveZeeloolSnowplowSdkConfig(
  input: ZeeloolSnowplowSdkConfigInput,
): SnowplowSdkConfig {
  const platform = input.deviceType === EPlatform.WEB ? 'pc' : 'wap';

  return {
    brand: 'zeelool',
    runtime: input.isInApp ? 'webview' : 'browser',
    enabled: input.enabled,
    collectorUrl: normalizeCollectorUrl(input.collectorUrl),
    appId: resolveAppId(input.appId, input.deviceType, input.environment),
    environment: normalizeEnvironment(input.environment),
    platform,
    gzipEnabled: input.gzipEnabled,
    ...(input.isInApp
      ? {}
      : {
          browserTrackerSource: 'classic' as const,
          browserIdentityPolicy: 'require-sensors-anonymous-id' as const,
        }),
    adapters: input.adapters,
  };
}
